import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ABSMark from "../assets/ABS-Logo/Vector/Vector.png";
import { useLoading } from "../context/LoadingProvider";

/**
 * TEMPORARY — "Welcome to our new journey" intro for the redesign launch.
 * Shown once per browser (localStorage key below), above the app.
 * To remove: delete this file and its <WelcomeIntro /> line in app/Layout.jsx.
 */

const STORAGE_KEY = "abs-welcome-v2";
const FONT_HREF = "https://fonts.googleapis.com/css2?family=Sacramento&display=swap";
const W1 = { text: "Welcome", size: 200, y: 190, start: 0.3, step: 0.26, dur: 0.9 };
const W2 = { text: "to our new journey", size: 84, y: 305, start: 2.3, step: 0.11, dur: 0.6 };
const AUTO_DISMISS_MS = 7500;
const REDUCED_DISMISS_MS = 2000;
const FADE_MS = 800;

const seen = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return true; // storage blocked: don't show
  }
};

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Character offsets + total width of a line, measured from a hidden SVG text. */
const measure = (svg, line) => {
  const t = document.createElementNS("http://www.w3.org/2000/svg", "text");
  t.setAttribute("font-family", "Sacramento");
  t.setAttribute("font-size", line.size);
  t.textContent = line.text;
  svg.appendChild(t);
  const offsets = [...line.text].map((_, i) => (i === 0 ? 0 : t.getSubStringLength(0, i)));
  const width = t.getComputedTextLength();
  svg.removeChild(t);
  return { offsets, width };
};

const CSS = `
@keyframes welcomeInk { to { stroke-dashoffset: 0; } }
@keyframes welcomeFill { to { fill-opacity: 1; } }
@keyframes welcomeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes welcomeGlow {
  0%, 100% { filter: drop-shadow(0 0 4px rgba(255,138,31,.45)); }
  50% { filter: drop-shadow(0 0 14px rgba(255,138,31,.95)) drop-shadow(0 0 28px rgba(255,138,31,.45)); }
}
.welcome-letter { stroke-dasharray: 1400; stroke-dashoffset: 1400; fill-opacity: 0; stroke-linecap: round; stroke-linejoin: round; }
`;

const WelcomeIntro = () => {
  const { showSplash, showRefresh } = useLoading();
  // Wait for the splash / refresh screens so the animation isn't hidden.
  return showSplash || showRefresh ? null : <Intro />;
};

const Intro = () => {
  const [visible, setVisible] = useState(() => !seen());
  const [closing, setClosing] = useState(false);
  const [layout, setLayout] = useState(null); // { W, lines: [{...line, x0, offsets}] }
  const measureSvg = useRef(null);
  const reduced = useRef(reducedMotion());

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    setClosing(true);
    setTimeout(() => setVisible(false), FADE_MS);
  }, []);

  // Load the script font for this component only.
  useEffect(() => {
    if (!visible) return undefined;
    // The @font-face must exist before fonts.load() can fetch it, otherwise
    // the glyphs get measured with a fallback font.
    const stylesheetReady = new Promise((resolve) => {
      let link = document.querySelector(`link[href="${FONT_HREF}"]`);
      if (link?.sheet) return resolve();
      if (!link) {
        link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = FONT_HREF;
        document.head.appendChild(link);
      }
      link.addEventListener("load", resolve, { once: true });
      link.addEventListener("error", resolve, { once: true });
    });
    let cancelled = false;
    const run = async () => {
      await stylesheetReady;
      try {
        await document.fonts.load("120px Sacramento");
        await document.fonts.ready;
      } catch {
        /* fall back to whatever loaded */
      }
      if (cancelled || !measureSvg.current) return;
      const m1 = measure(measureSvg.current, W1);
      const m2 = measure(measureSvg.current, W2);
      const W = Math.ceil(Math.max(m1.width, m2.width) + 80);
      setLayout({
        W,
        lines: [
          { ...W1, offsets: m1.offsets, x0: (W - m1.width) / 2 },
          { ...W2, offsets: m2.offsets, x0: (W - m2.width) / 2 },
        ],
      });
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  // Dismiss on any key, or automatically.
  useEffect(() => {
    if (!visible || closing) return undefined;
    const onKey = () => dismiss();
    window.addEventListener("keydown", onKey);
    const t = setTimeout(dismiss, reduced.current ? REDUCED_DISMISS_MS : AUTO_DISMISS_MS);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [visible, closing, dismiss]);

  if (!visible) return null;
  const still = reduced.current;

  return createPortal(
    <div
      role="dialog"
      aria-label="Welcome to our new journey"
      onClick={dismiss}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        background: "radial-gradient(900px 600px at 50% 40%, rgba(255,128,26,.14), transparent 70%), #070708",
        opacity: closing ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease`,
      }}
    >
      <style>{CSS}</style>
      {/* Hidden SVG used only for measuring glyph offsets. */}
      <svg ref={measureSvg} width="0" height="0" style={{ position: "absolute", visibility: "hidden" }} aria-hidden="true" />

      {layout && (
        <svg
          viewBox={`0 0 ${layout.W} 340`}
          style={{ width: "min(92vw, 980px)", height: "auto", filter: "drop-shadow(0 0 22px rgba(255,138,31,.32))", overflow: "visible" }}
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="welcomeGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#FFD29A" />
              <stop offset="45%" stopColor="#FF8A1F" />
              <stop offset="100%" stopColor="#F2620A" />
            </linearGradient>
          </defs>
          {layout.lines.map((line, li) => {
            const paint = li === 0 ? "url(#welcomeGrad)" : "#F6F4F1";
            let n = 0;
            return [...line.text].map((ch, i) => {
              if (ch === " ") return null;
              const delay = line.start + n * line.step;
              n += 1;
              return (
                <text
                  key={`${li}-${i}`}
                  className={still ? undefined : "welcome-letter"}
                  x={line.x0 + line.offsets[i]}
                  y={line.y}
                  fontFamily="Sacramento"
                  fontSize={line.size}
                  fill={paint}
                  stroke={paint}
                  strokeWidth={line.size * 0.012}
                  style={
                    still
                      ? undefined
                      : {
                          animation: `welcomeInk ${line.dur}s cubic-bezier(.45,0,.25,1) ${delay}s forwards, welcomeFill .7s ease ${
                            delay + line.dur * 0.55
                          }s forwards`,
                        }
                  }
                >
                  {ch}
                </text>
              );
            });
          })}
        </svg>
      )}

      <img
        src={ABSMark}
        alt="ABS"
        style={{
          height: 30,
          marginTop: 36,
          opacity: still ? 1 : 0,
          animation: still ? undefined : "welcomeUp .6s ease 4.6s forwards, welcomeGlow 2.6s ease-in-out 5.2s infinite",
        }}
      />
    </div>,
    document.body,
  );
};

export default WelcomeIntro;
